// Sugalarm — Cycle 126 guided Insulin entry.
//
// Replaces the cycle-121 single units field + "Long-acting (basal)"
// toggle with a three-button choice — Bolus / Correction / Basal — that
// asks for the dose first and the context second. Each path submits a
// single `POST /api/treatments` row with the existing shape plus the
// additive `dose_intent` (Bolus → 'meal', Correction → 'correction';
// Basal carries no dose_intent). The classification is therefore
// persisted by the user's choice and authoritative over the cycle-125
// inference (see `isCorrectionBolus`).
//
// Numeric entry is variant-aware: the Pi / iPad kiosk (a fixed appliance
// with no OS keyboard) keeps the custom <Numpad> in a slide-in overlay;
// web (cycle 293) + mobile use native <input> so the OS keyboard / number
// pad is raised on focus.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

import * as treatmentsApi from "../../api/treatments";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { ChevronDownIcon } from "../../components/ui/icons";
import { KioskNumpadOverlay } from "./KioskNumpadOverlay";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { isAndroidTabletClassRuntime } from "../../lib/deviceClass";
import { hapticSelect, hapticSuccess, hapticTap } from "../../lib/haptics";
import { ACCEPT_IMAGE_CAMERA, ACCEPT_IMAGE_GALLERY } from "../../lib/imageAccept";
import {
  CARBS_INVALID_MESSAGE,
  parseInsulinUnits,
  parseOptionalCarbGrams,
} from "../../lib/logEntryValidation";
import { setPendingPhotoFile } from "../../lib/photoHandoff";
import type { TreatmentCreateIn } from "../../lib/types";
import { armBolusUnits, type BolusPhotoHandoff } from "./bolusPhotoHandoff";
import styles from "./InsulinFlow.module.css";
import {
  openPhotoInput,
  photoBlockedByAlarm,
  PHOTO_BLOCKED_LINE,
  usePhotoBlockedByAlarm,
} from "../../lib/nativeCamera";
import { cameraOnlyDevice } from "../../lib/photoSource";

export type { BolusPhotoReturn } from "./bolusPhotoHandoff";

// Fast-acting products (Bolus + Correction paths).
const FAST_ACTING = [
  "Humalog",
  "Novolog",
  "Fiasp",
  "Apidra",
  "Lyumjev",
  "Admelog",
  "Afrezza",
  "Regular",
];
// Long-acting products (Basal path) + their typical per-dose duration in
// minutes (pre-selects the matching duration chip; the user can override).
const LONG_ACTING: { name: string; durationMin: number }[] = [
  { name: "Lantus", durationMin: 1440 },
  { name: "Basaglar", durationMin: 1440 },
  { name: "Semglee", durationMin: 1440 },
  { name: "Toujeo", durationMin: 1440 },
  { name: "Levemir", durationMin: 1440 },
  { name: "Tresiba", durationMin: 2520 },
  { name: "NPH", durationMin: 720 },
];

const DURATION_PRESETS: { label: string; min: number }[] = [
  { label: "12h", min: 720 },
  { label: "24h", min: 1440 },
  { label: "36h", min: 2160 },
  { label: "42h", min: 2520 },
];

const CORRECTION_REASONS = ["Bringing a high down", "Pre-bolus", "Other"];

type Path = "bolus" | "correction" | "basal";

interface InsulinFlowProps {
  variant: FrameVariant;
  isLandscape: boolean;
  units: "mg/dL" | "mmol/L";
  h24: boolean;
  photoEnabled: boolean;
  /** Cycle 452: when Log is open over `/now`, photo entry is not offered and
   *  this line stands where the photo buttons would have been. Null
   *  everywhere else. */
  photoNotice?: string | null;
  timestampIso: string;
  /** The shared "when" backdate control (built by LogScreen). Rendered
   *  on every Insulin path so the timestamp control stays present. */
  whenControl: ReactNode;
  /** Deep link `/log?tab=insulin&basal=1` → open the Basal path. */
  initialBasal: boolean;
  /** Photo-return state when arriving back from the Bolus photo step.
   *  Read ONCE, at mount (cycle 451.5: LogScreen now has it at its own
   *  mount, so this is no longer one render late). */
  photoReturn: BolusPhotoHandoff | null;
  /** Cycle 451.5: fired once after a handoff is read, so LogScreen drops it
   *  and a later mount of this flow can never be seeded from it again. */
  onPhotoReturnConsumed?: () => void;
  /** Fired after a successful log; LogScreen handles confirm/confetti/
   *  recent-reload/when-reset. */
  onLogged: (msg: { confirm: string; congrats: string | null }) => void;
  /** Cycle 133 (kiosk only): the portal host overlaying the Today's-
   *  entries card, where the slide-in numpad renders. Null off-kiosk. */
  kioskPadPortal?: HTMLElement | null;
}

// In-app product dropdown (NOT a native <select> — opens as a panel).
function ProductDropdown({
  products,
  value,
  onPick,
  constrainPanel = false,
}: {
  products: string[];
  value: string;
  onPick: (name: string) => void;
  /** Cycle 159 (kiosk/web): cap the open panel to the room below it so
   *  it can't push the entry surface into a vertical scroll. Off on
   *  mobile — that layout is byte-identical to pre-159. */
  constrainPanel?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [other, setOther] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  // Cycle 159: the open panel is absolutely positioned, so any part of
  // it past the entry surface's scroll-container bottom still counts as
  // scrollable overflow — opening the dropdown used to push the kiosk
  // column / web root into a vertical scroll. Cap the panel's height to
  // the room actually below the trigger (the option list scrolls
  // internally past the cap, as it always did past 240px). Measured in
  // visual px and converted back to layout px so the cycle-53 kiosk
  // scale transform can't skew the cap.
  useLayoutEffect(() => {
    if (!open || !constrainPanel) return;
    const root = rootRef.current;
    const panel = panelRef.current;
    if (!root || !panel) return;
    let sc: HTMLElement | null = root.parentElement;
    while (sc) {
      const oy = getComputedStyle(sc).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      sc = sc.parentElement;
    }
    const boundBottom = sc
      ? sc.getBoundingClientRect().bottom
      : window.innerHeight;
    const rect = root.getBoundingClientRect();
    const scale = root.offsetHeight > 0 ? rect.height / root.offsetHeight : 1;
    const available = (boundBottom - rect.bottom - 10) / (scale || 1);
    panel.style.maxHeight = `${Math.max(96, Math.min(240, Math.floor(available)))}px`;
  }, [open, constrainPanel]);

  const known = products.includes(value);

  return (
    <div className={styles.dropdown} ref={rootRef}>
      <button
        type="button"
        className={styles.dropdownBtn}
        onClick={() => {
          hapticTap();
          setOpen((o) => !o);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`${styles.dropdownBtnText} ${
            value ? "" : styles.dropdownBtnPlaceholder
          }`}
        >
          {value || "Select insulin…"}
        </span>
        <ChevronDownIcon className={styles.dropdownChev} />
      </button>
      {open && (
        <div className={styles.dropdownPanel} role="listbox" ref={panelRef}>
          {products.map((p) => (
            <button
              key={p}
              type="button"
              role="option"
              aria-selected={value === p}
              className={`${styles.dropdownItem} ${
                value === p ? styles.dropdownItemActive : ""
              }`}
              onClick={() => {
                hapticSelect();
                setOther(false);
                onPick(p);
                setOpen(false);
              }}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            className={`${styles.dropdownItem} ${styles.dropdownOther}`}
            onClick={() => {
              hapticSelect();
              setOther(true);
              onPick("");
              setOpen(false);
            }}
          >
            Other — type it in
          </button>
        </div>
      )}
      {(other || (!known && value !== "")) && (
        <input
          type="text"
          className={styles.textInput}
          style={{ marginTop: 8 }}
          placeholder="Insulin name"
          value={value}
          onChange={(e) => onPick(e.target.value)}
          maxLength={40}
        />
      )}
    </div>
  );
}

export function InsulinFlow({
  variant,
  isLandscape,
  units,
  h24,
  photoEnabled,
  photoNotice = null,
  timestampIso,
  whenControl,
  initialBasal,
  photoReturn,
  onPhotoReturnConsumed,
  onLogged,
  kioskPadPortal,
}: InsulinFlowProps) {
  const nav = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const isKiosk = variant === "kiosk";
  const isMobile = variant === "mobile";
  // Cycle 199: the Android-tablet kiosk uses the MOBILE native-input path
  // (native <input inputmode> → OS number pad) instead of the custom
  // Numpad / KioskNumpadOverlay. The Pi kiosk keeps the overlay.
  const nativeEntry = isKiosk && isAndroidTabletClassRuntime();
  // void unused props that are reserved for parity with the other tabs.
  void isLandscape;
  void h24;
  void units;

  // Cycle 451.5: a valid handoff opens the Bolus path with the meal filled
  // in, exactly as a hand entry would have left it: on the meal step when
  // the person's own typed units came back valid (verbatim), otherwise on
  // the units step, empty. An invalid one opens the chooser with the manual
  // Carbs field's message.
  const meal = photoReturn?.kind === "meal" ? photoReturn.meal : null;
  const [path, setPath] = useState<Path | null>(
    meal ? "bolus" : initialBasal ? "basal" : null,
  );
  const [bolusStep, setBolusStep] = useState<"units" | "meal">(
    meal && meal.units !== "" ? "meal" : "units",
  );
  const [unitsStr, setUnitsStr] = useState<string>(meal?.units ?? "");
  const [carbsStr, setCarbsStr] = useState<string>(
    meal ? String(meal.carbs_g) : "",
  );
  const [mealDesc, setMealDesc] = useState<string>(meal?.description ?? "");
  const [reason, setReason] = useState<string | null>(null);
  const [label, setLabel] = useState<string>(meal?.label ?? "");
  const [durationMin, setDurationMin] = useState<number>(1440);
  const [photoProvenance, setPhotoProvenance] = useState<
    Record<string, unknown> | null
  >(meal?.provenance ?? null);
  const [error, setError] = useState<string | null>(
    photoReturn?.kind === "invalid" ? CARBS_INVALID_MESSAGE : null,
  );
  const [busy, setBusy] = useState(false);
  // Cycle 133 (kiosk): whether the slide-in numpad overlay is open.
  const [padOpen, setPadOpen] = useState(false);

  // Cycle 451.5: the handoff is one-shot. It was read above, at mount; tell
  // LogScreen so it drops it (a later mount must never be seeded from it).
  const handoffAtMount = useRef(photoReturn);
  useEffect(() => {
    if (handoffAtMount.current) onPhotoReturnConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cycle 451.5: while the form holds a photo meal, any move to a new
  // history entry other than a REPLACE (Back out of the flow, a push to
  // another Log entry) resets it — Log stays mounted across those, so
  // nothing from the meal may linger. The arrival's own REPLACE keeps it.
  // Same keying as 451.4's confirmation clear.
  const photoSeeded = useRef(meal !== null);
  const seedEntry = useRef(location.key);
  useEffect(() => {
    if (seedEntry.current === location.key) return;
    seedEntry.current = location.key;
    if (navType !== "REPLACE" && photoSeeded.current) resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navType]);

  const resetAll = () => {
    photoSeeded.current = false;
    setPath(null);
    setBolusStep("units");
    setUnitsStr("");
    setCarbsStr("");
    setMealDesc("");
    setReason(null);
    setLabel("");
    setDurationMin(1440);
    setPhotoProvenance(null);
    setError(null);
    setPadOpen(false);
  };

  const choosePath = (p: Path) => {
    hapticSelect();
    setError(null);
    setPath(p);
    setBolusStep("units");
    setPadOpen(false);
  };

  const back = () => {
    hapticTap();
    setError(null);
    setPadOpen(false);
    if (path === "bolus" && bolusStep === "meal") {
      setBolusStep("units");
      return;
    }
    resetAll();
  };

  // ─── numeric field binding for the kiosk/web Numpad ────────────────
  // The Numpad edits whichever numeric field is active for the step.
  const activeIsCarbs = path === "bolus" && bolusStep === "meal";
  const padValue = activeIsCarbs ? carbsStr : unitsStr;
  const setPadValue = (v: string) =>
    activeIsCarbs ? setCarbsStr(v) : setUnitsStr(v);
  const padUnit = activeIsCarbs ? "g" : "u";

  const onLog = async (body: TreatmentCreateIn, congrats: string | null) => {
    setBusy(true);
    setError(null);
    const r = await treatmentsApi.create(body);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    if (isMobile) hapticSuccess();
    resetAll();
    onLogged({ confirm: "Logged insulin.", congrats });
  };

  const submitBolus = () => {
    const u = Number(unitsStr);
    if (!Number.isFinite(u) || u <= 0) {
      setError("Enter insulin in units.");
      return;
    }
    const desc = mealDesc.trim();
    // Cycle 453.4 (operator decision): a typed 0 is an entered amount and
    // saves `carbs_g: 0`; an empty (or whitespace) field saves no key at all
    // ("not entered").
    const c = parseOptionalCarbGrams(carbsStr);
    const body: TreatmentCreateIn = {
      event_type: "insulin",
      timestamp: timestampIso,
      insulin_u: u,
      insulin_kind: "bolus",
      dose_intent: "meal",
    };
    if (c !== null) body.carbs_g = c;
    if (desc) body.notes = desc;
    if (label.trim()) body.insulin_label = label.trim();
    if (photoProvenance) body.provenance = photoProvenance;
    const word = u === 1 ? "unit" : "units";
    const congrats = desc
      ? `You logged ${u} ${word} of insulin for ${desc}.`
      : `You logged ${u} ${word} of insulin.`;
    void onLog(body, congrats);
  };

  const submitCorrection = () => {
    const u = Number(unitsStr);
    if (!Number.isFinite(u) || u <= 0) {
      setError("Enter insulin in units.");
      return;
    }
    const body: TreatmentCreateIn = {
      event_type: "insulin",
      timestamp: timestampIso,
      insulin_u: u,
      insulin_kind: "bolus",
      dose_intent: "correction",
    };
    if (reason) body.notes = reason;
    if (label.trim()) body.insulin_label = label.trim();
    const word = u === 1 ? "unit" : "units";
    void onLog(body, `You logged a ${u}-${word} correction.`);
  };

  const submitBasal = () => {
    const u = Number(unitsStr);
    if (!Number.isFinite(u) || u <= 0) {
      setError("Enter insulin in units.");
      return;
    }
    const body: TreatmentCreateIn = {
      event_type: "insulin",
      timestamp: timestampIso,
      insulin_u: u,
      insulin_kind: "basal",
      insulin_duration_min: durationMin,
    };
    if (label.trim()) body.insulin_label = label.trim();
    const word = u === 1 ? "unit" : "units";
    void onLog(
      body,
      `You logged ${u} ${word} of ${label.trim() || "long-acting"} insulin.`,
    );
  };

  // Cycle 340: hand a picked File straight to the meal-photo → carbs flow
  // via the same handoff module the Carbs-tab CTAs use, so it enters at
  // the `preview` phase directly — no extra click on a landing screen.
  // Mobile/web get two explicit targets (camera vs library); the
  // Android-tablet kiosk keeps a single button whose no-capture input
  // already opens the native "Take photo / Choose from gallery" dialog
  // (see LogScreen's tablet CTA — same reasoning, no native change here).
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const tabletPhotoInputRef = useRef<HTMLInputElement>(null);
  const onPhotoPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return; // chooser cancelled
    setPendingPhotoFile(file);
    // Cycle 451.5: the typed units travel in a one-shot memory slot, never
    // in the photo screen's history entry (see bolusPhotoHandoff.ts).
    nav("/log/photo", {
      state: { bolusReturn: { token: armBolusUnits(unitsStr, label) } },
    });
  };
  // Cycle 455: meal_photo to the kiosk APK right before the click; the
  // tablet chip is disabled while any alarm sounds (lib/nativeCamera).
  const photoBlocked = usePhotoBlockedByAlarm();
  const openCamera = () => openPhotoInput(cameraInputRef.current, "meal_photo");
  // Cycle 458: the kiosk APK is camera-only (lib/photoSource) — no gallery.
  const mealCameraOnly = cameraOnlyDevice();
  const openGallery = () => {
    if (mealCameraOnly) return;
    galleryInputRef.current?.click();
  };
  const openTabletPhotoChooser = () => {
    if (photoBlockedByAlarm()) return;
    openPhotoInput(tabletPhotoInputRef.current, "meal_photo");
  };

  // ─── A numeric input: native on mobile, readout+Numpad on kiosk/web ─
  const numericInput = (
    kind: "units" | "carbs",
    placeholder: string,
  ): ReactNode => {
    const val = kind === "carbs" ? carbsStr : unitsStr;
    const setter = kind === "carbs" ? setCarbsStr : setUnitsStr;
    const unit = kind === "carbs" ? "g" : "u";
    // Cycle 199: mobile AND the Android-tablet kiosk use the native input.
    // Cycle 293: web joins them — the desktop Log uses the OS keyboard, so
    // any non-(Pi/iPad)-kiosk surface gets the native <input>. Only the Pi /
    // iPad kiosk (no OS keyboard) keeps the readout + <Numpad> overlay.
    if (!isKiosk || nativeEntry) {
      return (
        <input
          type="text"
          className={styles.numInput}
          inputMode={kind === "carbs" ? "numeric" : "decimal"}
          placeholder={placeholder}
          value={val}
          onChange={(e) => {
            const filtered =
              kind === "carbs"
                ? e.target.value.replace(/[^0-9]/g, "")
                : e.target.value.replace(/[^0-9.]/g, "");
            setter(filtered);
          }}
          maxLength={5}
        />
      );
    }
    // Cycle 133 — kiosk: the readout is a BUTTON that slides the numpad
    // overlay in over Today's entries. web: a passive readout the inline
    // Numpad edits.
    if (isKiosk) {
      return (
        <button
          type="button"
          className={`${styles.readout} ${styles.readoutBtn} ${
            padOpen ? styles.readoutActive : ""
          }`}
          onClick={() => setPadOpen(true)}
          aria-label={`Enter ${kind === "carbs" ? "carbs" : "units"}`}
        >
          <span
            className={`${styles.readoutValue} ${
              val === "" ? styles.readoutPlaceholder : ""
            }`}
          >
            {val === "" ? "—" : val}
          </span>
          <span className={styles.readoutUnit}>{unit}</span>
        </button>
      );
    }
    return (
      <div className={styles.readout}>
        <span
          className={`${styles.readoutValue} ${
            val === "" ? styles.readoutPlaceholder : ""
          }`}
        >
          {val === "" ? "—" : val}
        </span>
        <span className={styles.readoutUnit}>{unit}</span>
      </div>
    );
  };

  // ─── choice screen ─────────────────────────────────────────────────
  if (path === null) {
    return (
      <div className={styles.flow}>
        {/* Cycle 451.5: only an invalid meal-photo handoff reaches the
            chooser with a message (choosing a path clears it). */}
        {error && <div className={styles.errorMsg}>{error}</div>}
        <div className={styles.choice}>
          {/* Cycle 132: order Correction → Meal → Basal so the no-food
              option leads. The "Bolus" button writes dose_intent='meal' (the
              path id stays `bolus` and still writes dose_intent='meal'). */}
          <button
            type="button"
            className={styles.choiceBtn}
            onClick={() => choosePath("correction")}
          >
            <span className={styles.choiceTitle}>Correction</span>
            <span className={styles.choiceSub}>no food — bring a high down</span>
          </button>
          <button
            type="button"
            className={styles.choiceBtn}
            onClick={() => choosePath("bolus")}
          >
            <span className={styles.choiceTitle}>Bolus</span>
            <span className={styles.choiceSub}>insulin with food</span>
          </button>
          <button
            type="button"
            className={styles.choiceBtn}
            onClick={() => choosePath("basal")}
          >
            <span className={styles.choiceTitle}>Basal</span>
            <span className={styles.choiceSub}>long-acting</span>
          </button>
        </div>
      </div>
    );
  }

  // Cycle 159 (kiosk + web only): the product selector and the units
  // field share ONE two-column row instead of stacking — the reclaimed
  // block is what brings the entry surface back inside its height
  // budget. Mobile keeps the exact pre-159 stacked DOM (single-column,
  // native-OS-pad layout — byte-identical).
  const pairRow = (a: ReactNode, b: ReactNode): ReactNode =>
    isMobile ? (
      <>
        {a}
        {b}
      </>
    ) : (
      <div className={styles.fieldRow}>
        <div className={styles.fieldCol}>{a}</div>
        <div className={styles.fieldCol}>{b}</div>
      </div>
    );

  // ─── per-path controls (variant-agnostic inner content) ────────────
  let title = "";
  let controls: ReactNode = null;
  let onSave: () => void = () => {};
  let saveLabel = "Save";

  if (path === "bolus" && bolusStep === "units") {
    title = "Bolus — units";
    controls = (
      <div className={styles.controls}>
        {pairRow(
          <div>
            <label className={styles.fieldLabel}>How many units?</label>
            {numericInput("units", "0")}
          </div>,
          <div>
            <label className={styles.fieldLabel}>Insulin (optional)</label>
            <ProductDropdown
              products={FAST_ACTING}
              value={label}
              onPick={setLabel}
              constrainPanel={!isMobile}
            />
          </div>,
        )}
      </div>
    );
    onSave = () => {
      // Cycle 451.5: the shared units check (the photo handoff uses it too).
      if (parseInsulinUnits(unitsStr) === null) {
        setError("Enter insulin in units.");
        return;
      }
      setError(null);
      hapticSelect();
      setBolusStep("meal");
      setPadOpen(false); // Cycle 133: close the pad on step advance.
    };
    saveLabel = "Next — the meal ›";
  } else if (path === "bolus" && bolusStep === "meal") {
    title = "Bolus — the meal";
    controls = (
      <div className={styles.controls}>
        <span className={styles.summaryChip}>{unitsStr || "—"} u</span>
        {/* Cycle 299: meal + carbs share ONE row (like the Carbs tab) so the
            carbs field isn't stranded at the bottom of a tall form behind the
            soft keyboard. Meal takes 3/4 of the row, carbs 1/4, matched
            heights. Mobile keeps its stacked single-column layout. */}
        {(() => {
          const mealNode = (
            <div>
              <label className={styles.fieldLabel}>What's the meal?</label>
              <input
                type="text"
                className={styles.textInput}
                placeholder="Sandwich, oatmeal, etc."
                value={mealDesc}
                onChange={(e) => setMealDesc(e.target.value)}
                maxLength={120}
              />
            </div>
          );
          const carbsNode = (
            <div>
              <label className={styles.fieldLabel}>Carbs</label>
              {numericInput("carbs", "0")}
            </div>
          );
          return isMobile ? (
            <>
              {mealNode}
              {carbsNode}
            </>
          ) : (
            <div className={styles.mealRow}>
              {mealNode}
              {carbsNode}
            </div>
          );
        })()}
        {/* Cycle 200: allow the Bolus food-step photo entry on the Android
            tablet kiosk (rear camera + native in-app capture); the Pi/iPad
            kiosk stay excluded. Web/mobile unchanged. Cycle 340: mobile/web
            get two direct targets; the tablet kiosk keeps its one button
            wired to its own native chooser dialog. */}
        {photoNotice != null &&
          (!isKiosk || isAndroidTabletClassRuntime()) && (
            <p className={styles.photoNotice} data-testid="log-overlay-photo-line">
              {photoNotice}
            </p>
          )}
        {photoEnabled && !isKiosk && (
          <div className={styles.chipRow}>
            <button type="button" className={styles.chip} onClick={openCamera}>
              <span className={styles.photoEmoji} aria-hidden>
                📷
              </span>
              Take Photo
            </button>
            {mealCameraOnly ? null : (
              <button type="button" className={styles.chip} onClick={openGallery}>
                <span className={styles.photoEmoji} aria-hidden>
                  🖼️
                </span>
                Choose from Library
              </button>
            )}
            <input
              ref={cameraInputRef}
              type="file"
              accept={ACCEPT_IMAGE_CAMERA}
              capture="environment"
              onChange={onPhotoPicked}
              style={{ display: "none" }}
            />
            {mealCameraOnly ? null : (
              <input
                ref={galleryInputRef}
                type="file"
                accept={ACCEPT_IMAGE_GALLERY}
                onChange={onPhotoPicked}
                style={{ display: "none" }}
              />
            )}
          </div>
        )}
        {photoEnabled && isKiosk && isAndroidTabletClassRuntime() && (
          <>
            <button
              type="button"
              className={styles.chip}
              onClick={openTabletPhotoChooser}
              disabled={photoBlocked}
              title={photoBlocked ? PHOTO_BLOCKED_LINE : undefined}
              aria-label={photoBlocked ? `Photo of meal. ${PHOTO_BLOCKED_LINE}` : undefined}
            >
              <span className={styles.photoEmoji} aria-hidden>
                📷
              </span>
              Photo of meal
            </button>
            {/* Cycle 458: `capture` on the kiosk APK only, so a chooser whose
                cycle-455 purpose never paired routes to the in-app camera,
                never the "Choose from gallery" dialog. The standard phone
                app on a tablet keeps the capture-less input (its picker). */}
            <input
              ref={tabletPhotoInputRef}
              type="file"
              accept={ACCEPT_IMAGE_GALLERY}
              capture={mealCameraOnly ? "environment" : undefined}
              onChange={onPhotoPicked}
              style={{ display: "none" }}
            />
          </>
        )}
        {whenControl}
      </div>
    );
    onSave = submitBolus;
    saveLabel = "Log bolus";
  } else if (path === "correction") {
    title = "Correction — units";
    controls = (
      <div className={styles.controls}>
        {pairRow(
          <div>
            <label className={styles.fieldLabel}>How many units?</label>
            {numericInput("units", "0")}
          </div>,
          <div>
            <label className={styles.fieldLabel}>Insulin (optional)</label>
            <ProductDropdown
              products={FAST_ACTING}
              value={label}
              onPick={setLabel}
              constrainPanel={!isMobile}
            />
          </div>,
        )}
        <div>
          <label className={styles.fieldLabel}>Reason (optional)</label>
          <div className={styles.chipRow}>
            {CORRECTION_REASONS.map((rz) => (
              <button
                key={rz}
                type="button"
                className={`${styles.chip} ${
                  reason === rz ? styles.chipActive : ""
                }`}
                onClick={() => {
                  hapticSelect();
                  setReason(reason === rz ? null : rz);
                }}
              >
                {rz}
              </button>
            ))}
          </div>
        </div>
        {whenControl}
      </div>
    );
    onSave = submitCorrection;
    saveLabel = "Log correction";
  } else {
    // basal
    title = "Basal — long-acting";
    const basalProduct = (
      <div>
        <label className={styles.fieldLabel}>Insulin</label>
        <ProductDropdown
          products={LONG_ACTING.map((p) => p.name)}
          value={label}
          onPick={(name) => {
            setLabel(name);
            const match = LONG_ACTING.find((p) => p.name === name);
            if (match) setDurationMin(match.durationMin);
          }}
          constrainPanel={!isMobile}
        />
      </div>
    );
    const basalLasts = (
      <div>
        <label className={styles.fieldLabel}>Lasts</label>
        <div className={styles.chipRow}>
          {DURATION_PRESETS.map((d) => (
            <button
              key={d.min}
              type="button"
              className={`${styles.chip} ${
                durationMin === d.min ? styles.chipActive : ""
              }`}
              onClick={() => {
                hapticSelect();
                setDurationMin(d.min);
              }}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
    );
    const basalUnits = (
      <div>
        <label className={styles.fieldLabel}>Units</label>
        {numericInput("units", "0")}
      </div>
    );
    // Mobile keeps the pre-159 order (product → Lasts → units) DOM-
    // identical; kiosk/web pair product+units on row one with the
    // duration chips beneath.
    controls = (
      <div className={styles.controls}>
        {isMobile ? (
          <>
            {basalProduct}
            {basalLasts}
            {basalUnits}
          </>
        ) : (
          <>
            {pairRow(basalProduct, basalUnits)}
            {basalLasts}
          </>
        )}
        {whenControl}
      </div>
    );
    onSave = submitBasal;
    saveLabel = "Log basal";
  }

  const head = (
    <div className={styles.pathHead}>
      {/* Cycle 162: shared glass BackButton, same `back` handler (no
          parent-screen name — this steps the guided flow back). */}
      <BackButton label="Back" onBack={back} solid />
      <span className={styles.pathTitle}>{title}</span>
    </div>
  );

  const saveBtn = (
    <Button onClick={onSave} disabled={busy} full>
      {busy ? "Logging…" : saveLabel}
    </Button>
  );

  const feedback = error ? <div className={styles.errorMsg}>{error}</div> : null;

  // ─── kiosk: single-column controls; the numpad is a slide-in overlay
  //     over Today's entries (cycle 133), opened by tapping a readout ──
  // Cycle 199: the Android-tablet kiosk (`nativeEntry`) skips this overlay
  // path and falls through to the native-input layout (same as mobile) —
  // the readouts are native <input> already, raising the OS number pad.
  if (isKiosk && !nativeEntry) {
    return (
      <div className={styles.flow}>
        {head}
        {controls}
        {feedback}
        {saveBtn}
        <KioskNumpadOverlay
          open={padOpen}
          label={activeIsCarbs ? "Carbs" : "Insulin units"}
          value={padValue}
          unit={padUnit}
          onChange={setPadValue}
          allowDecimal={!activeIsCarbs}
          maxLength={5}
          onDone={() => setPadOpen(false)}
          portalTarget={kioskPadPortal ?? null}
        />
      </div>
    );
  }

  // ─── web + mobile (portrait/landscape): single column, native inputs ─
  // Cycle 293: web no longer renders the inline <Numpad> — its numeric
  // fields are native keyboard <input>s (see numericInput above), so it
  // shares this native-input layout with mobile.
  return (
    <div className={styles.flow}>
      {head}
      {controls}
      {feedback}
      {saveBtn}
    </div>
  );
}
